import * as z from "zod"

const XML_ATTRIBUTE = /([A-Za-z_:][\w:.-]*)\s*=\s*(?:"([^"]*)"|'([^']*)')/g

const XML_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
}

/** Matches the attribute list of an XML tag, for embedding in a tag regex. */
export const XML_ATTRIBUTES_SOURCE = `(?:\\s+[A-Za-z_:][\\w:.-]*\\s*=\\s*(?:"[^"]*"|'[^']*'))*`

/** A non-negative integer attribute, which XML can only carry as its digits. */
export const xmlInt = () => z.string().regex(/^\d+$/).transform(Number)

export function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

/**
 * Escapes an attribute value. Tabs, line breaks, and every space after the first in a run become
 * character references: XML parsers normalize literal tabs and line breaks to spaces, a newline
 * would split the tag across Markdown lines, and `md` collapses runs of spaces.
 */
function escapeXmlAttribute(value: string): string {
  return escapeXml(value).replace(/[\t\n\r]|(?<= ) /g, (char) => `&#${char.charCodeAt(0)};`)
}

/**
 * Renders each pair as ` name="value"`, for the inside of an opening tag. Values that carry
 * nothing — null, undefined, false, and "" — are omitted.
 */
export function formatXmlAttributes(attributes: Record<string, unknown>): string {
  return Object.entries(attributes)
    .filter(([, value]) => value !== null && value !== undefined && value !== false && value !== "")
    .map(([name, value]) => ` ${name}="${escapeXmlAttribute(String(value))}"`)
    .join("")
}

export function unescapeXml(value: string): string {
  return value.replace(
    /&(?:(amp|lt|gt|quot|apos)|#(\d+)|#x([\da-f]+));/gi,
    (match, name, dec, hex) => {
      if (name) return XML_ENTITIES[name] ?? match
      const codePoint = dec ? Number(dec) : parseInt(hex, 16)
      return codePoint <= 0x10ffff ? String.fromCodePoint(codePoint) : match
    },
  )
}

/**
 * Reads `name="value"` pairs from a tag's attribute list and validates them against `schema`.
 * XML attributes are always strings, so the schema's input must be too; it can still transform
 * them (e.g. to an enum). Returns null when the attributes don't satisfy the schema.
 */
export function parseXmlAttributes<T>(
  source: string,
  schema: z.ZodType<T, Partial<Record<string, string>>>,
): T | null {
  const attributes: Record<string, string> = {}
  for (const match of source.matchAll(XML_ATTRIBUTE)) {
    attributes[match[1]] = unescapeXml(match[2] ?? match[3])
  }
  const parsed = schema.safeParse(attributes)
  return parsed.success ? parsed.data : null
}
